import { ATTENTION_STATUS_ROLE_IDS } from 'partner360/entities/attentionStatuses'
import type { IAggregatedAttentionStatus } from 'partner360/entities/attentionStatuses'

import {
  buildAttentionOwnerNodes,
  UNASSIGNED_MANAGER_KEY,
  UNASSIGNED_MANAGER_LABEL,
  UNASSIGNED_PBM_KEY,
  UNASSIGNED_PBM_LABEL,
} from './buildAttentionOwnerNodes'
import type { IAttentionGeoNode, IAttentionOwnerAssignment, IAttentionPartner } from './types'

/** How the full-screen navigator lists the partners in view. */
export type TAttentionNavigatorMode = 'people' | 'geo' | 'managers' | 'pbms' | 'partners'

/** Navigator modes by role: a PBM has only their partners, a manager their people, a geo lead
 *  every cut of their scope. Managers and geo leads get no partner list, as the screen already
 *  shows and searches partners. The first mode is the one the view opens on. */
export const getAttentionNavigatorModes = (roleId?: string | null): TAttentionNavigatorMode[] => {
  switch (roleId) {
    case ATTENTION_STATUS_ROLE_IDS.pbm:
      return ['partners']
    case ATTENTION_STATUS_ROLE_IDS.manager:
      return ['people', 'geo']
    case ATTENTION_STATUS_ROLE_IDS.geoLead:
    default:
      return ['geo', 'managers', 'pbms']
  }
}

export interface IAttentionNavigatorNode {
  key: string
  /** Name the payload owns; absent on a heading whose words are ours. */
  label?: string
  /** Translation key of a heading, e.g. `My PBMs`. */
  labelKey?: string
  partnerIds: string[]
  children: IAttentionNavigatorNode[]
  /** The PBM or manager the node stands for, who can be reminded. */
  owner?: { email: string; name: string; kind: 'pbm' | 'manager' }
  /** A heading that groups nodes and is not itself picked. */
  isHeading?: boolean
}

/** Open statuses of one partner, the most urgent first. */
export interface IAttentionPartnerOverview {
  partner: IAttentionPartner
  open: IAggregatedAttentionStatus[]
  /** Highest severity among `open`; `0` when nothing is open. */
  worstSeverity: number
}

export const buildAttentionPartnerOverviews = (
  partners: IAttentionPartner[],
  statuses: IAggregatedAttentionStatus[]
): Map<string, IAttentionPartnerOverview> => {
  const overviews = new Map<string, IAttentionPartnerOverview>(
    partners.map((partner) => [partner.partnerId, { partner, open: [], worstSeverity: 0 }])
  )

  statuses.forEach((status) =>
    status.partnerIds.forEach((partnerId) => overviews.get(partnerId)?.open.push(status))
  )

  overviews.forEach((overview) => {
    overview.open.sort(
      (left, right) => right.severity - left.severity || left.sortOrder - right.sortOrder
    )
    overview.worstSeverity = overview.open[0]?.severity ?? 0
  })

  return overviews
}

const UNASSIGNED_KEYS = new Set([UNASSIGNED_PBM_KEY, UNASSIGNED_MANAGER_KEY])

const fromOwnerNode = (
  prefix: 'pbm' | 'manager',
  node: IAttentionGeoNode
): IAttentionNavigatorNode => ({
  key: `${prefix}:${node.itemId}`,
  label: node.name,
  partnerIds: node.partnerIds,
  children: [],
  owner: UNASSIGNED_KEYS.has(node.itemId)
    ? undefined
    : { email: node.itemId, name: node.name, kind: prefix },
})

export const fromAttentionGeoNodes = (nodes: IAttentionGeoNode[]): IAttentionNavigatorNode[] =>
  nodes.map((node) => ({
    key: `geo:${node.itemId}`,
    label: node.name,
    partnerIds: node.partnerIds,
    children: fromAttentionGeoNodes(node.children),
  }))

interface IOwnerTreeParams {
  partners: IAttentionPartner[]
  pbmAssignments: IAttentionOwnerAssignment[]
  managerAssignments: IAttentionOwnerAssignment[]
}

export const buildAttentionPbmTree = ({ partners, pbmAssignments }: IOwnerTreeParams) =>
  buildAttentionOwnerNodes({
    partners,
    assignments: pbmAssignments,
    unassigned: { itemId: UNASSIGNED_PBM_KEY, name: UNASSIGNED_PBM_LABEL },
  }).map((node) => fromOwnerNode('pbm', node))

/** Managers, each with the PBMs of their own partners under them. */
export const buildAttentionManagerTree = (params: IOwnerTreeParams): IAttentionNavigatorNode[] =>
  buildAttentionOwnerNodes({
    partners: params.partners,
    assignments: params.managerAssignments,
    unassigned: { itemId: UNASSIGNED_MANAGER_KEY, name: UNASSIGNED_MANAGER_LABEL },
  }).map((node) => {
    const managed = new Set(node.partnerIds)
    const manager = fromOwnerNode('manager', node)

    return {
      ...manager,
      children: buildAttentionPbmTree({
        ...params,
        partners: params.partners.filter(({ partnerId }) => managed.has(partnerId)),
      }).map((child) => ({ ...child, key: `${manager.key}/${child.key}` })),
    }
  })

interface IPeopleTreeParams extends IOwnerTreeParams {
  currentUserEmail: string
  labelKeys: { myPbms: string; otherManagers: string }
}

/**
 * A manager's own PBMs first, then every other manager with theirs. A user the payload names as
 * no partner's manager gets the plain manager tree.
 */
export const buildAttentionPeopleTree = ({
  currentUserEmail,
  labelKeys,
  ...params
}: IPeopleTreeParams): IAttentionNavigatorNode[] => {
  const managers = buildAttentionManagerTree(params)
  const email = currentUserEmail.toLowerCase()
  const own = managers.find(({ owner }) => owner?.email.toLowerCase() === email)

  if (!own) {
    return managers
  }

  const others = managers.filter((manager) => manager !== own)

  return [
    {
      key: 'people:my-pbms',
      labelKey: labelKeys.myPbms,
      partnerIds: own.partnerIds,
      children: own.children,
      isHeading: true,
    },
    ...(others.length > 0
      ? [
          {
            key: 'people:other-managers',
            labelKey: labelKeys.otherManagers,
            partnerIds: [...new Set(others.flatMap(({ partnerIds }) => partnerIds))],
            children: others,
            isHeading: true,
          },
        ]
      : []),
  ]
}

/** The node with `key` and the nodes above it, outermost first; empty when it is not found. */
export const findAttentionNavigatorPath = (
  nodes: IAttentionNavigatorNode[],
  key: string
): IAttentionNavigatorNode[] => {
  for (const node of nodes) {
    if (node.key === key) {
      return [node]
    }

    const below = findAttentionNavigatorPath(node.children, key)

    if (below.length > 0) {
      return [node, ...below]
    }
  }

  return []
}
